import React, { useEffect, useState } from 'react'
import { Form, Switch, message, Button, PageHeader, Divider } from 'antd'
import { useMutation, useApolloClient, useQuery } from '@apollo/client'
import { UPDATE_SETTINGS_AND_SAMEDAY_PICKUP_MOV } from '../actions'
import { UPDATE_ORDER_TYPE_SETTING_CAPACITY } from '../PreOrderSettings/actions'
import {
  QUERY_STORE_ORDER_TYPE_CAPACITY,
  OrderTypeCapacity
} from '../LocationQueries'
import UpdateNotice from '../../Utils/UpdateNotice'
import { LocationSettingProps } from '../Settings'
import { Store } from 'rc-field-form/lib/interface'
import TimeSlots, {
  pickup_timeslots
} from '../PreOrderSettings/General/TimeSlots'
import CapacityRules from '../PreOrderSettings/General/CapacityRules'
import MinimumOrderValueInput from './MinimumOrderValueInput'
import AutoAccept from '../PreOrderSettings/General/AutoAccept'
import { storeOrderTypeSettingsInitialValue } from '../utils'
import { StoreOrderTypeSettings } from '@slerp/controls'
import { isManager, isAdmin, useSession } from 'packages/@slerp/accounts'
import { FormActions } from '../commonStyles'
const { useForm } = Form

type FormType = {
  disabled?: boolean
}

interface InitialValuesType {
  pickup_interval: number
  pickup_timeslot_order_limit: number | null
  products_per_timeslot: null
  capacity_per_product: null
  capacity_per_modifier: null
  auto_accept: StoreOrderTypeSettings | Record<string, unknown>
}

const PickupSettings = (props: LocationSettingProps & FormType) => {
  const { user } = useSession()
  const canManage = isAdmin(user) || isManager(user)

  const { merchant, store, disabled = false } = props
  const [form] = useForm()

  const [updateOrderTypeSettingCapacity] = useMutation(
    UPDATE_ORDER_TYPE_SETTING_CAPACITY,
    { fetchPolicy: 'no-cache' }
  )

  const { data: capacityData } = useQuery(QUERY_STORE_ORDER_TYPE_CAPACITY, {
    variables: { store_id: store.id },
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    const ots = capacityData?.store_order_type_settings?.find(
      (s: OrderTypeCapacity) =>
        s.fulfillment_type === 'pickup' && s.order_type === 'sameday'
    )
    if (!ots) return
    form.setFieldsValue({
      products_per_timeslot: ots.capacity_limit ?? null,
      capacity_per_product: ots.capacity_product_weight_default ?? null,
      capacity_per_modifier: ots.capacity_modifier_weight_default ?? null
    })
  }, [capacityData])

  const [enabled, setEnabled] = useState(
    store.settings.same_day_pickup || store.settings.scheduled_pickup
  )
  const [updateSetting, { loading, error }] = useMutation(
    UPDATE_SETTINGS_AND_SAMEDAY_PICKUP_MOV,
    {
      fetchPolicy: 'no-cache'
    }
  )
  const client = useApolloClient()

  const initialValues: InitialValuesType = {
    pickup_interval: store.settings.pickup_interval,
    pickup_timeslot_order_limit:
      store.settings.pickup_timeslot_order_limit || null,
    products_per_timeslot: null,
    capacity_per_product: null,
    capacity_per_modifier: null,
    auto_accept:
      store.store_order_type_settings?.find(
        (sot) =>
          sot.fulfillment_type === 'pickup' && sot.order_type === 'sameday'
      ) || storeOrderTypeSettingsInitialValue(store.id, 'pickup', 'sameday')
  }

  const onFinish = (values: Store) => {
    const {
      auto_accept,
      pickup_enabled,
      pickup_interval,
      pickup_timeslot_order_limit,
      sameday_pickup_minimum_order_value
    } = values
    form.validateFields().then(() => {
      message.loading('Updating... Please wait.')
      Promise.all([
        updateSetting({
          variables: {
            merchant_id: merchant.id,
            id: store.id,
            settings: {
              same_day_pickup: pickup_enabled,
              scheduled_pickup: pickup_enabled,
              pickup_interval,
              pickup_timeslot_order_limit: pickup_timeslot_order_limit ?? null
            },
            pickup_sameday_mov:
              sameday_pickup_minimum_order_value === undefined
                ? store.sameday_pickup_minimum_order_value || 0
                : sameday_pickup_minimum_order_value,
            order_type_settings:
              auto_accept ??
              (store.store_order_type_settings?.find(
                (sot) =>
                  sot.fulfillment_type === 'pickup' &&
                  sot.order_type === 'sameday'
              ) ||
                storeOrderTypeSettingsInitialValue(
                  store.id,
                  'pickup',
                  'sameday'
                ))
          }
        }),
        updateOrderTypeSettingCapacity({
          variables: {
            store_id: store.id,
            order_type: 'sameday',
            fulfillment_type: 'pickup',
            capacity_limit: values.products_per_timeslot ?? null,
            capacity_product_weight_default:
              values.capacity_per_product ?? null,
            capacity_modifier_weight_default:
              values.capacity_per_modifier ?? null
          }
        })
      ]).then(() => {
        message.destroy()
        client.resetStore()
        message.success('Pickup settings updated', 1)
      })
    })
  }

  return (
    <>
      <Form
        data-testid='pickup-settings-form'
        layout='vertical'
        colon={false}
        form={form}
        onFinish={onFinish}
        initialValues={initialValues}
        disabled={!canManage}
      >
        <PageHeader title='Pickup Settings' className='settings-title' />
        <Form.Item name='pickup_enabled' label='Enable Pickup'>
          <Switch
            data-testid='same-day-switch'
            onChange={setEnabled}
            defaultChecked={enabled}
            disabled={disabled}
          />
        </Form.Item>

        {enabled && (
          <>
            <MinimumOrderValueInput
              inputName='sameday_pickup_minimum_order_value'
              defaultValue={
                Number(store.sameday_pickup_minimum_order_value) || 0
              }
              disabled={disabled}
              testId='sameday-pickup-minimum-order-value'
            />

            <TimeSlots
              formName='pickup_interval'
              value={store.settings.pickup_interval}
              handleChange={(value) =>
                form.setFieldsValue({ pickup_interval: value })
              }
              timeslots={pickup_timeslots}
              disabled={disabled}
            />

            <AutoAccept
              inputName='auto_accept'
              formRef={form}
              isPreOrder={false}
              isDelivery={false}
              disabled={!canManage}
            />
            <Divider />
            <CapacityRules
              orderLimitFieldName='pickup_timeslot_order_limit'
              disabled={disabled}
            />
          </>
        )}
        <Divider />
        <FormActions wrapperCol={{ span: 24 }}>
          <Button
            title='Save'
            htmlType='submit'
            className='_center-vertical _ml-auto'
            disabled={disabled}
          >
            Save
          </Button>
          <UpdateNotice updating={loading} updateError={error} />
        </FormActions>
      </Form>
    </>
  )
}

export default PickupSettings
